import React, { useEffect, useState } from 'react'
import {
  Container,
  TextBody,
  ToastContext,
  TextTitle,
  ListContainer
} from '@pmi/pmi-iqos-ds-react'
import {
  cookie,
  dispatchPopupPromotionAlreadyUsed,
  mapBundleListForDisplay,
  fillBundlesWithExtraData,
  parseTranslations,
  CommonTranslations,
  goTo,
  constants,
  analytics,
  PublishedComponent,
  htmlTranslation,
  extractErrorMessage,
  canUseDOM
} from '@pmi/pmi-iqos-shared-react'
import { api } from '@dcx-common/services'
import {
  ProductBanner,
  ProductCardBundle,
  ProductListFilters
} from '@dcx-common/components'
import ProductBundleListTranslations from './product-bundle-list.json'

const ANALYTICS_LIST_NAME = 'Smart selector'

const ProductBundleListComponent = ({
  translations,
  bundles,
  mainCategory,
  category,
  categoryId,
  bundleDetailPath,
  filtersInfo = {},
  logError
}) => {
  const [loadingExtraData, setLoadingExtraData] = useState(false)
  const [bundlesValue, setBundles] = useState(bundles)
  const [mainCategoryValue] = useState(mainCategory)
  const [filterOptions, setFilterOptions] = useState()
  const [bundlesFiltered, setBundlesFiltered] = useState([])
  const [sortOptions, setSortOptions] = useState()

  const toastContext = ToastContext.getContextActions()
  const popupToast = toastContext.popupToast

  const handleAddToCart = (bundle, index) => {
    const url = bundleDetailPath ? bundleDetailPath + bundle.id : ''
    analytics.productClickBundle(bundle, index, mainCategoryValue.category, url)
    url && goTo(url)
  }

  const loadData = async () => {
    await getBundlesExtraData(categoryId)
    analyticsProductListImpression()
    setFiltersInfo()
  }

  const analyticsProductListImpression = () => {
    if (bundlesValue && bundlesValue.length) {
      analytics.productListImpression(bundlesValue, ANALYTICS_LIST_NAME)
    }
  }

  const listenLoginSuccessEvent = (kill = false) => {
    const e = constants.REACT_LOGIN_SUCCESS_EVENT
    const f = loadData
    if (kill) {
      window.removeEventListener(e, f)
    } else {
      window.addEventListener(e, f)
    }
  }

  useEffect(() => {
    listenLoginSuccessEvent()
    return () => {
      listenLoginSuccessEvent(true)
    }
  }, [listenLoginSuccessEvent])

  useEffect(() => {
    api.setErrorTranslations(translations)
    loadData()
  }, [])

  const getBundlesExtraData = async category => {
    setLoadingExtraData(true)
    try {
      const res = await api.cart.getDevicesBundles({
        params: { categoryid: category, stock: true, promo: true }
      })
      const updatedBundles = fillBundlesWithExtraData(bundlesValue, res.chains)
      setBundles(updatedBundles)
      dispatchPopupPromotionAlreadyUsed(
        cookie.getSpiceId(),
        updatedBundles,
        popupToast,
        translations,
        Container,
        TextBody
      )
    } catch (err) {
      logError(err)
    } finally {
      setLoadingExtraData(false)
    }
  }

  const setFiltersInfo = () => {
    if (!filtersInfo.hideFilters) {
      setFilterOptions(filtersInfo.filterOptions || [])
      setSortOptions(filtersInfo.sortOptions || [])
    }
  }

  const promoBanners =
    mainCategoryValue && mainCategoryValue.promotional_banners

  // If some filter is applied or sorted, show the filtered bundles list, otherwise show all bundles
  const bundlesToPresent = bundlesFiltered.length
    ? bundlesFiltered
    : bundlesValue
  return (
    <>
      <Container
        display={Container.displays.flex}
        flexDirection={[
          Container.flexDirections.column,
          Container.flexDirections.column,
          Container.flexDirections.row
        ]}>
        {!filtersInfo.hideFilters ? (
          <Container
            width={['full', 'full', '3/12']}
            pr={['0', '0', '8']}
            pb={['10', '10', '0']}>
            <ProductListFilters
              translations={translations}
              filterOptions={filterOptions}
              sortOptions={sortOptions}
              originalList={bundlesValue}
              setListFiltered={setBundlesFiltered}
            />
          </Container>
        ) : null}
        <Container
          width={filtersInfo.hideFilters ? 'full' : ['full', 'full', '9/12']}>
          {mainCategoryValue && promoBanners && promoBanners.length > 0 && (
            <ProductBanner image={promoBanners[0]} />
          )}
          <Container>
            {bundlesToPresent && bundlesToPresent.length ? (
              <Container>
                <Container pb="4">
                  <TextTitle
                    heading={TextTitle.headings.h3}
                    data-testing="ProductBundleList_TextTitle_BundleProducts">
                    {`${translations.tk_productResults} (${
                      (bundlesToPresent && bundlesToPresent.length) || 0
                    })`}
                  </TextTitle>
                </Container>
                <Container pb="14">
                  <TextBody
                    style={TextBody.styles.strong}
                    size={TextBody.sizes.small}
                    acceptsHtml
                    value={
                      category && category.description
                        ? category.description
                        : (mainCategory && mainCategory.description) || ''
                    }></TextBody>
                </Container>
                <ListContainer
                  columns={[1, 2, 3]}
                  xBetween="8"
                  yBetween="20"
                  fullWidth>
                  {bundlesToPresent &&
                    bundlesToPresent.map((bundle, index) => (
                      <Container
                        key={bundle.id}
                        display={Container.displays.flex}
                        flexDirection={Container.flexDirections.column}
                        fullWidth>
                        <ProductCardBundle
                          bundle={bundle}
                          stock
                          onAddToCart={bundle => {
                            handleAddToCart(bundle, index)
                          }}
                          translations={translations}
                          buttonAddToCartLabel={translations.tk_selectAnOption}
                          testingPrefix="ProductBundleList"
                          loading={loadingExtraData}
                          bundleDetailPath={bundleDetailPath}
                        />
                        {/** To help only for tests */}
                        {canUseDOM && window.filters_debug ? (
                          <Container>
                            {'Attributes: '}
                            {JSON.stringify(bundle.optionsAttributes)}
                          </Container>
                        ) : null}
                      </Container>
                    ))}
                </ListContainer>
              </Container>
            ) : (
              <Container
                fullWidth
                display={Container.displays.flex}
                justifyContent={Container.justifyContentValues.center}
                py="20">
                <TextBody
                  {...htmlTranslation(translations, 'tk_noProductsFound')}
                />
              </Container>
            )}
          </Container>
        </Container>
      </Container>
    </>
  )
}

const ProductBundleList = props => {
  return (
    <ToastContext>
      <PublishedComponent
        innerComponent={ProductBundleListComponent}
        {...ComponentDefaultProps}
        {...props}
      />
    </ToastContext>
  )
}

const ComponentTranslations = {
  ...ProductBundleListTranslations,
  ...CommonTranslations
}

const ComponentDefaultProps = {
  componentName: 'ProductBundleList',
  translations: {
    ...parseTranslations(ComponentTranslations, true)
  }
}

ProductBundleList.getInitialProps = async req => {
  return PublishedComponent.getGlobalInitialProps({
    context: req && req.query && req.query.context,
    componentName: ComponentDefaultProps.componentName,
    defaultTranslations: ComponentDefaultProps.translations,
    translationsConfig: ComponentTranslations,
    getSpecificInitialProps: async () => {
      const hideFilters = req.query.hide_filters === 'true'
      const bundlesData = await ProductBundleList.getDevicesBundles(
        req.query.category,
        req,
        hideFilters
      )
      return {
        error: bundlesData.error || null,
        bundles: bundlesData.bundles || [],
        mainCategory: bundlesData.mainCategory,
        category: bundlesData.category,
        categoryId: req.query.category,
        bundleDetailPath:
          (req.body.extra && req.body.extra.bundle_detail_path) || '',
        filtersInfo: bundlesData.filtersInfo || { hideFilters }
      }
    }
  })
}

ProductBundleList.getDevicesBundles = async (category, req, hideFilters) => {
  let res = {}
  try {
    res = await api.cart.getDevicesBundles({
      req,
      params: {
        categoryid: category,
        ...(!hideFilters && { filterOptions: true, sortOptions: true })
      }
    })
    if (!res) return {}
  } catch (error) {
    if (error.data && error.data.Data) {
      res = error.data.Data
    }
    res.error = {
      message: extractErrorMessage(error),
      status: error.status
    }
  }
  return ProductBundleList.getDevicesBundlesInfo(res, req, hideFilters)
}

ProductBundleList.getDevicesBundlesInfo = async (res, req, hideFilters) => {
  const bundleList = (req.body.extra && req.body.extra.bundle_list) || null
  const bundles = mapBundleListForDisplay(res.chains, true, bundleList)
  return {
    error: res.error || null,
    bundles: bundles || [],
    mainCategory: res.main_category,
    category: res.category,
    filtersInfo: {
      hideFilters,
      filterOptions: res.filterOptions || [],
      sortOptions: res.sortOptions || []
    }
  }
}

export default ProductBundleList
